import { useCallback, useEffect, useMemo, useState } from "react";
import BookingStats from "@/pages/admin/components/BookingStats";
import BookingsToolbar from "@/pages/admin/components/BookingsToolbar";
import BookingsTable from "@/pages/admin/components/BookingsTable";
import BookingDetailPanel, { type NudgeResult } from "@/pages/admin/components/BookingDetailPanel";
import {
  deleteBooking,
  fetchBookings,
  formatBookingDate,
  formatDateTime,
  statusMeta,
  typeLabel,
  updateBookingNotes,
  updateBookingStatus,
  type Booking,
  type BookingStatus,
} from "@/lib/bookings";
import { exportToCsv, exportToPdf, type ExportColumn } from "@/pages/admin/exportUtils";
import { supabase } from "@/lib/supabase";

/** Absolute link to the public "Share Your Experience" form, honouring the app base path. */
function reviewFormUrl(): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const prefix = basePath ? `/${basePath}` : "";
  return `${window.location.origin}${prefix}/reviews#share`;
}

const exportColumns: ExportColumn<Booking>[] = [
  { header: "Booking ID", value: (booking) => `RTG-${10000 + booking.id}` },
  { header: "Guest", value: (booking) => booking.name },
  { header: "Email", value: (booking) => booking.email },
  { header: "Phone", value: (booking) => booking.phone || "" },
  { header: "Type", value: (booking) => typeLabel(booking.booking_type) },
  { header: "Experience", value: (booking) => booking.subject || "" },
  { header: "Preferred date", value: (booking) => formatBookingDate(booking.preferred_date) },
  { header: "Group size", value: (booking) => (booking.group_size ? String(booking.group_size) : "") },
  { header: "Status", value: (booking) => statusMeta(booking.status).label },
  { header: "Received", value: (booking) => formatDateTime(booking.created_at) },
];

const exportBtn =
  "inline-flex items-center gap-2 border border-background-300 text-foreground-800 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer";

export default function AdminBookings() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<BookingStatus | "all">("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const data = await fetchBookings();
      setBookings(data);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn't load your bookings. Please try again.",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleStatusChange = async (id: number, status: BookingStatus) => {
    const previous = bookings;
    const target = bookings.find((item) => item.id === id);
    const wasConfirmed = target?.status === "confirmed";
    setBookings((list) => list.map((item) => (item.id === id ? { ...item, status } : item)));
    try {
      await updateBookingStatus(id, status);
      // When a booking is first confirmed, nudge the guest to share their
      // experience. Fire-and-forget so a mail hiccup never affects the booking;
      // it no-ops until email sending is configured.
      if (status === "confirmed" && !wasConfirmed && target?.email) {
        supabase.functions
          .invoke("send-review-nudge", {
            body: {
              name: target.name,
              email: target.email,
              subject: target.subject || "",
              review_url: reviewFormUrl(),
            },
          })
          .catch(() => {});
      }
    } catch {
      setBookings(previous);
      setError("We couldn't update that booking. Please try again.");
    }
  };

  const handleSaveNotes = async (id: number, notes: string): Promise<boolean> => {
    try {
      await updateBookingNotes(id, notes);
      setBookings((list) =>
        list.map((item) => (item.id === id ? { ...item, admin_notes: notes } : item)),
      );
      return true;
    } catch {
      setError("We couldn't save that note. Please try again.");
      return false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteBooking(id);
      setBookings((list) => list.filter((item) => item.id !== id));
      setSelectedId(null);
    } catch {
      setError("We couldn't delete that booking. Please try again.");
    }
  };

  /**
   * Manually re-send the "share your experience" nudge for a booking, without
   * touching its status. Returns how it went so the panel can show feedback.
   */
  const handleSendNudge = async (id: number): Promise<NudgeResult> => {
    const target = bookings.find((item) => item.id === id);
    if (!target?.email) return "error";
    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-review-nudge", {
        body: {
          name: target.name,
          email: target.email,
          subject: target.subject || "",
          review_url: reviewFormUrl(),
        },
      });
      if (fnError) return "error";
      const skipped = (data as { skipped?: boolean } | null)?.skipped;
      return skipped ? "skipped" : "sent";
    } catch {
      return "error";
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return bookings.filter((booking) => {
      if (statusFilter !== "all" && booking.status !== statusFilter) return false;
      if (typeFilter !== "all" && booking.booking_type !== typeFilter) return false;
      if (!query) return true;
      return [
        booking.name,
        booking.email,
        booking.phone || "",
        booking.subject || "",
        booking.message || "",
        booking.itinerary || "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [bookings, search, statusFilter, typeFilter]);

  const selected = bookings.find((item) => item.id === selectedId) || null;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7 md:mb-9">
        <div>
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
            Operations
          </span>
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-2">
            Bookings &amp; itineraries
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-2xl">
            Every experience request and custom itinerary lands here in real time. Update statuses,
            keep internal notes and follow up with guests.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => exportToCsv("rythugaru-bookings", exportColumns, filtered)}
            className={exportBtn}
          >
            <i className="ri-file-excel-2-line"></i>
            CSV
          </button>
          <button
            type="button"
            onClick={() =>
              exportToPdf({
                title: "Bookings",
                subtitle: "All experience requests and itineraries currently shown",
                columns: exportColumns,
                rows: filtered,
              })
            }
            className={exportBtn}
          >
            <i className="ri-file-pdf-2-line"></i>
            PDF
          </button>
        </div>
      </div>

      <BookingStats bookings={bookings} />

      {error ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-accent-100 border border-accent-300 rounded-lg px-4 py-3.5 mt-5">
          <p className="flex items-start gap-2 text-sm text-accent-900 flex-1">
            <i className="ri-error-warning-line text-lg"></i>
            <span>{error}</span>
          </p>
          <button
            type="button"
            onClick={() => load(true)}
            className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm font-semibold px-4 py-2.5 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer self-start"
          >
            <i className="ri-refresh-line"></i>
            Retry
          </button>
        </div>
      ) : null}

      <div className="mt-5 md:mt-6 space-y-5">
        <BookingsToolbar
          search={search}
          onSearch={setSearch}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          typeFilter={typeFilter}
          onTypeFilter={setTypeFilter}
          resultCount={filtered.length}
          onRefresh={() => load(true)}
          refreshing={refreshing}
        />

        {loading ? (
          <div className="bg-background-50 border border-background-200 rounded-lg py-20 flex flex-col items-center gap-3">
            <i className="ri-loader-4-line text-3xl animate-spin text-primary-500"></i>
            <p className="text-sm text-foreground-600">Loading bookings…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-background-50 border border-background-200 rounded-lg py-16 px-6 text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
              <i className="ri-calendar-check-line text-3xl"></i>
            </span>
            <h3 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">
              {bookings.length === 0 ? "No bookings yet" : "No bookings match"}
            </h3>
            <p className="mt-3 text-foreground-600 leading-relaxed max-w-md mx-auto">
              {bookings.length === 0
                ? "As soon as a guest requests an experience or builds an itinerary, it will appear here."
                : "Try a different search or clear the filters to see everything."}
            </p>
          </div>
        ) : (
          <BookingsTable
            bookings={filtered}
            onSelect={setSelectedId}
            onStatusChange={handleStatusChange}
          />
        )}
      </div>

      {selected ? (
        <BookingDetailPanel
          booking={selected}
          onClose={() => setSelectedId(null)}
          onStatusChange={handleStatusChange}
          onSaveNotes={handleSaveNotes}
          onDelete={handleDelete}
          onSendNudge={handleSendNudge}
        />
      ) : null}
    </div>
  );
}